import React, { useMemo, useState } from 'react'

import {
    Box,
    Button,
    Card,
    Chip,
    Grid,
    IconButton,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import ArrowBackIcon from '@material-ui/icons/ArrowBack'
import AddIcon from '@material-ui/icons/Add'
import CloudUploadIcon from '@material-ui/icons/CloudUpload'
import LocationOnIcon from '@material-ui/icons/LocationOn'
import SchoolIcon from '@material-ui/icons/School'
import { Link } from 'react-router-dom'

import { EzosLoader } from '../../../components'
import { useApartmentProspects } from '../../../hooks/Outreach/useApartmentProspects'
import { useUser } from '../../../hooks/useUser'
import {
    PROPERTY_DETAILS_TAB,
    propertyDetailsPath,
} from '../../../main/containers/settings/PropertyDetails/propertyDetailsTabUtils'
import { Routes } from '../../../navigation/config/routes'
import {
    Prospect,
    ServedSchoolProspectSummary,
} from '../../../models/Outreach/types'
import { ProspectingFunnelCard } from '../Overview/ProspectingFunnelCard'
import { FunnelMetrics } from '../types/leasingSeason.types'
import { SchoolProspectsTable } from '../../Outbound/Prospects/components/SchoolProspectsTable'
import { PoolProspectDetailDialog } from './components/PoolProspectDetailDialog'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(2.5),
    },
    statGrid: {
        marginBottom: 0,
    },
    statGridItem: {
        display: 'flex',
    },
    statTile: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        width: '100%',
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.background.paper,
    },
    statLabel: {
        fontSize: '0.625rem',
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    statValue: {
        marginTop: theme.spacing(0.5),
        fontSize: '1.5rem',
        fontWeight: 600,
        fontVariantNumeric: 'tabular-nums',
        color: theme.palette.text.primary,
    },
    statValueHighlight: {
        color: theme.palette.primary.main,
    },
    statHint: {
        marginTop: theme.spacing(0.5),
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        lineHeight: 1.4,
    },
    statAction: {
        marginTop: theme.spacing(1.5),
        alignSelf: 'flex-start',
    },
    card: {
        border: `1px solid ${theme.palette.divider}`,
    },
    cardHeader: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    cardTitle: {
        fontSize: '0.875rem',
        fontWeight: 600,
    },
    cardSubtitle: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.25),
    },
    schoolList: {
        padding: 0,
    },
    schoolRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(2),
        padding: theme.spacing(2),
        borderBottom: `1px solid ${theme.palette.divider}`,
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
        '&:last-child': {
            borderBottom: 'none',
        },
    },
    schoolIcon: {
        width: 44,
        height: 44,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.main + '14',
        color: theme.palette.primary.main,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    schoolMeta: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.25),
    },
    countGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(72px, auto))',
        gap: theme.spacing(3),
        textAlign: 'right',
        flexShrink: 0,
    },
    countValue: {
        fontSize: '1.125rem',
        fontWeight: 700,
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1.2,
    },
    countValueProspect: {
        color: theme.palette.primary.main,
    },
    countLabel: {
        fontSize: '0.625rem',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: theme.palette.text.secondary,
    },
    drillDownHeader: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    emptyState: {
        padding: theme.spacing(5),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    },
    primaryChip: {
        height: 20,
        fontSize: '0.65rem',
        marginLeft: theme.spacing(0.75),
    },
}))

function formatLocation(school: ServedSchoolProspectSummary): string | null {
    if (school.city && school.state) {
        return `${school.city}, ${school.state}`
    }
    if (school.city) return school.city
    if (school.state) return school.state
    return null
}

function formatDistance(miles: number | null): string | null {
    if (miles == null) return null
    return `${miles.toFixed(1)} mi away`
}

interface CollegesYouServeProps {
    readOnly?: boolean
    funnelMetrics: FunnelMetrics
}

export const CollegesYouServe: React.FC<CollegesYouServeProps> = ({
    readOnly = false,
    funnelMetrics,
}) => {
    const classes = useStyles()
    const { workspaceUser } = useUser()
    const apartmentId =
        workspaceUser?.active_workspace?.company_type === 'APARTMENT'
            ? workspaceUser.active_workspace.id
            : undefined

    const {
        summary,
        isLoadingSummary,
        browse,
        isLoadingBrowse,
        filters,
        setSchoolId,
        setSearch,
        setGraduationYears,
        setHasEmail,
        setHasPhone,
        setStatusSort,
        setPage,
        setPageSize,
        resetListFilters,
    } = useApartmentProspects(apartmentId)
    const [searchInput, setSearchInput] = useState('')
    const [drillDownSchoolId, setDrillDownSchoolId] = useState<number | null>(
        null,
    )
    const [selectedProspect, setSelectedProspect] = useState<Prospect | null>(
        null,
    )

    const servedSchools = useMemo(() => summary?.schools ?? [], [
        summary?.schools,
    ])

    const drillDownSchool = servedSchools.find(
        (s) => s.school_id === drillDownSchoolId,
    )

    const totalTargets = servedSchools.reduce(
        (sum, s) => sum + (s.target_count ?? 0),
        0,
    )
    const totalEnriched = servedSchools.reduce(
        (sum, s) => sum + (s.enriched_count ?? 0),
        0,
    )

    const handleOpenSchool = (schoolId: number) => {
        setDrillDownSchoolId(schoolId)
        setSchoolId(schoolId)
        setSearchInput('')
    }

    const handleBackToList = () => {
        setDrillDownSchoolId(null)
        setSearchInput('')
        resetListFilters()
        setSelectedProspect(null)
    }

    if (isLoadingSummary && !summary) {
        return <EzosLoader />
    }

    if (drillDownSchool) {
        return (
            <Box className={classes.root}>
                <Box className={classes.drillDownHeader}>
                    <IconButton
                        size="small"
                        onClick={handleBackToList}
                        aria-label="Back to schools"
                    >
                        <ArrowBackIcon />
                    </IconButton>
                    <Box flex={1} minWidth={0}>
                        <Typography variant="h6">
                            {drillDownSchool.effective_display_name}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {drillDownSchool.target_count.toLocaleString()}{' '}
                            targets ·{' '}
                            {drillDownSchool.enriched_count.toLocaleString()}{' '}
                            prospects
                        </Typography>
                    </Box>
                    {!readOnly && (
                        <Button
                            component={Link}
                            to={Routes.manage.prospects.upload.replace(
                                ':schoolId',
                                String(drillDownSchool.school_id),
                            )}
                            size="small"
                            color="primary"
                            variant="outlined"
                            startIcon={<CloudUploadIcon />}
                        >
                            Upload spreadsheet
                        </Button>
                    )}
                </Box>

                <SchoolProspectsTable
                    prospects={browse?.results ?? []}
                    totalCount={browse?.count ?? 0}
                    page={filters.page}
                    pageSize={filters.pageSize}
                    searchInput={searchInput}
                    onSearchInputChange={setSearchInput}
                    onSearchSubmit={() => setSearch(searchInput)}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    isLoading={isLoadingBrowse}
                    graduationYears={filters.graduationYears}
                    onGraduationYearsChange={setGraduationYears}
                    hasEmail={filters.hasEmail}
                    onHasEmailChange={setHasEmail}
                    hasPhone={filters.hasPhone}
                    onHasPhoneChange={setHasPhone}
                    statusSort={filters.statusSort}
                    onStatusSortChange={setStatusSort}
                    showStatusColumn
                    showCampaignColumn={false}
                    allowManualEnrich={false}
                    onRowClick={setSelectedProspect}
                />

                <PoolProspectDetailDialog
                    open={selectedProspect != null}
                    prospect={selectedProspect}
                    onClose={() => setSelectedProspect(null)}
                />
            </Box>
        )
    }

    return (
        <Box className={classes.root}>
            <Grid container spacing={2} className={classes.statGrid}>
                <Grid item xs={12} sm={4} className={classes.statGridItem}>
                    <Box className={classes.statTile}>
                        <Typography className={classes.statLabel}>
                            Colleges Served
                        </Typography>
                        <Typography className={classes.statValue}>
                            {servedSchools.length.toLocaleString()}
                        </Typography>
                        <Typography className={classes.statHint}>
                            Schools near your property where Lori is sourcing
                            students
                        </Typography>
                        {!readOnly && (
                            <>
                                <Typography className={classes.statHint}>
                                    Need to manage the colleges you&apos;re
                                    serving?
                                </Typography>
                                <Button
                                    component={Link}
                                    to={propertyDetailsPath(
                                        PROPERTY_DETAILS_TAB.schoolsServed,
                                    )}
                                    size="small"
                                    variant="outlined"
                                    startIcon={<AddIcon />}
                                    className={classes.statAction}
                                >
                                    Manage Schools Served
                                </Button>
                            </>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4} className={classes.statGridItem}>
                    <Box className={classes.statTile}>
                        <Typography className={classes.statLabel}>
                            Targets
                        </Typography>
                        <Typography className={classes.statValue}>
                            {totalTargets.toLocaleString()}
                        </Typography>
                        <Typography className={classes.statHint}>
                            Students at your colleges
                        </Typography>
                        <Typography className={classes.statHint}>
                            Lori automatically builds this for the colleges you
                            serve.
                        </Typography>
                    </Box>
                </Grid>
                <Grid item xs={12} sm={4} className={classes.statGridItem}>
                    <Box className={classes.statTile}>
                        <Typography className={classes.statLabel}>
                            Prospects
                        </Typography>
                        <Typography
                            className={`${classes.statValue} ${classes.statValueHighlight}`}
                        >
                            {totalEnriched.toLocaleString()}
                        </Typography>
                        <Typography className={classes.statHint}>
                            Enrich with phone or email
                        </Typography>
                        <Typography className={classes.statHint}>
                            Want more prospects?
                        </Typography>
                        <Button
                            component={Link}
                            to={`${Routes.manage.leasingSeason.root}?tab=tokens`}
                            size="small"
                            variant="outlined"
                            className={classes.statAction}
                        >
                            Manage tokens and automations
                        </Button>
                    </Box>
                </Grid>
            </Grid>

            <Card className={classes.card} variant="outlined">
                <Box className={classes.cardHeader}>
                    <Box>
                        <Typography className={classes.cardTitle}>
                            Prospects by school
                        </Typography>
                        <Typography className={classes.cardSubtitle}>
                            Browse targets and prospects for schools you serve,
                            or upload a spreadsheet from a school drill-down.
                        </Typography>
                    </Box>
                </Box>

                {servedSchools.length === 0 ? (
                    <Box className={classes.emptyState}>
                        <Typography variant="h6">
                            No colleges configured yet
                        </Typography>
                        <Typography variant="body2" paragraph>
                            Configure schools in Property Details → Schools
                            Served.
                        </Typography>
                        {!readOnly && (
                            <Button
                                component={Link}
                                to={propertyDetailsPath(
                                    PROPERTY_DETAILS_TAB.schoolsServed,
                                )}
                                color="primary"
                                variant="contained"
                            >
                                Configure schools
                            </Button>
                        )}
                    </Box>
                ) : (
                    <Box className={classes.schoolList}>
                        {servedSchools.map((school) => {
                            const location = formatLocation(school)
                            const distance = formatDistance(
                                school.inferred_distance_miles,
                            )
                            const enrollment =
                                school.fall_enrollment_total != null
                                    ? `${school.fall_enrollment_total.toLocaleString()} enrolled`
                                    : null

                            return (
                                <Box
                                    key={school.school_id}
                                    className={classes.schoolRow}
                                    onClick={() =>
                                        handleOpenSchool(school.school_id)
                                    }
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (
                                            e.key === 'Enter' ||
                                            e.key === ' '
                                        ) {
                                            handleOpenSchool(school.school_id)
                                        }
                                    }}
                                >
                                    <Box className={classes.schoolIcon}>
                                        <SchoolIcon />
                                    </Box>
                                    <Box flex={1} minWidth={0}>
                                        <Box display="flex" alignItems="center">
                                            <Typography
                                                variant="subtitle2"
                                                noWrap
                                            >
                                                {school.effective_display_name}
                                            </Typography>
                                            {school.is_primary && (
                                                <Chip
                                                    size="small"
                                                    label="Primary"
                                                    color="primary"
                                                    className={
                                                        classes.primaryChip
                                                    }
                                                />
                                            )}
                                        </Box>
                                        <Box className={classes.schoolMeta}>
                                            {location && (
                                                <Box
                                                    display="flex"
                                                    alignItems="center"
                                                    style={{ gap: 4 }}
                                                >
                                                    <LocationOnIcon
                                                        style={{
                                                            fontSize: 14,
                                                        }}
                                                    />
                                                    <span>{location}</span>
                                                </Box>
                                            )}
                                            {enrollment && (
                                                <>
                                                    {location && <span>·</span>}
                                                    <span>{enrollment}</span>
                                                </>
                                            )}
                                            {distance && (
                                                <>
                                                    {(location ||
                                                        enrollment) && (
                                                        <span>·</span>
                                                    )}
                                                    <span>{distance}</span>
                                                </>
                                            )}
                                        </Box>
                                    </Box>
                                    <Box className={classes.countGrid}>
                                        <Box>
                                            <Typography
                                                className={classes.countValue}
                                            >
                                                {(
                                                    school.target_count ?? 0
                                                ).toLocaleString()}
                                            </Typography>
                                            <Typography
                                                className={classes.countLabel}
                                            >
                                                Targets
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography
                                                className={`${classes.countValue} ${classes.countValueProspect}`}
                                            >
                                                {(
                                                    school.enriched_count ?? 0
                                                ).toLocaleString()}
                                            </Typography>
                                            <Typography
                                                className={classes.countLabel}
                                            >
                                                Prospects
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            )
                        })}
                    </Box>
                )}
            </Card>

            <ProspectingFunnelCard metrics={funnelMetrics} />
        </Box>
    )
}
